import { TimeType } from '@repo/shared/dtos/generic'
import { getLocale } from '@repo/ui/hooks'
import { z } from '@repo/ui/lang'
import { clsx, type ClassValue } from 'clsx'
import { differenceInMilliseconds, format, formatDistance } from 'date-fns'
import { FormEvent } from 'react'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Capitalizes the first letter of the given string.
 *
 * @param str - The string to be capitalized.
 * @returns The input string with its first letter capitalized. If the input string is empty or undefined, returns 'Error: missing string'. If the input is not a string, returns 'Error: not a string'.
 */
export function Cfl(str: string): string {
  if (typeof str !== 'string') return 'Error: not a string'
  else if (str) return str[0]?.toUpperCase() + str.slice(1)
  return 'Error: missing string'
}

export function createMessagesByDay<T extends { createdAtTime: TimeType }>(messages: T[]) {
  const map = new Map<string, T[]>()
  messages.forEach((m) => {
    const date = format(new Date(m.createdAtTime), 'MM/dd/yy')
    if (!map.get(date)) map.set(date, [])
    const currentDateMap = map.get(date)
    currentDateMap?.push(m)
  })
  return map
}

export function createTasksByDueDate<T extends { dueDate: TimeType }>(tasks: T[]) {
  const map = new Map<string, T[]>()
  if (!tasks) return map
  tasks.forEach((task) => {
    if (!task.dueDate) return
    const date = format(new Date(task.dueDate), 'MM/dd/yy')
    if (!map.get(date)) map.set(date, [])
    const currentDateMap = map.get(date)
    currentDateMap?.push(task)
  })
  return map
}

export const onInput =
  (cb: (v: string) => void) => (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if ('value' in event.target && typeof event.target.value === 'string') cb(event.target.value)
    else window.console.error('Error: missing event.target.value')
  }

// Do not export this enum, it is only used in this file
enum Status {
  Uninitialized = 'Uninitialized',
  Loading = 'Loading',
  Error = 'Error',
}

export const DataStatus = Object.freeze({
  // Enum
  Error: Status.Error,
  Loading: Status.Loading,
  Uninitialized: Status.Uninitialized,

  // Checkers
  isFulfilled<T>(data: AsyncData<T>): data is T {
    return (
      data !== undefined &&
      data !== null &&
      data !== this.Loading &&
      data !== this.Error &&
      data !== this.Uninitialized
    )
  },
  isLoading<T>(data: AsyncData<T>): boolean {
    return data === this.Loading
  },
  isError<T>(data: AsyncData<T>): boolean {
    return data === this.Error || data === null
  },
  isUninitialized<T>(data: AsyncData<T>): boolean {
    return data === this.Uninitialized
  },
})

export type AsyncData<T> = T | Status.Error | Status.Loading | Status.Uninitialized

export const genWidths = (size: number, min: number = 45, max: number = 85): number[] => {
  const availableSizes = (() => {
    const array: number[] = []
    for (let i = min; i <= max; i += 5) array.push(i)
    return array
  })()
  const result: number[] = []
  for (let i = 0; i < size; i++) {
    const randomIndex = Math.floor(Math.random() * availableSizes.length)
    result.push(availableSizes[randomIndex] ?? 80)
  }
  return result
}

export const zCleanString = z.string().nonempty().trim()

export const sortByKey =
  <T extends Record<string, any>>(key: keyof T, order: 'asc' | 'desc' = 'asc') =>
  (a: T, b: T) => {
    const valueA = a[key]
    const valueB = b[key]
    const multiplier = order === 'asc' ? 1 : -1

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB) * multiplier
    } else if (typeof valueA === 'number' && typeof valueB === 'number') {
      return (valueA - valueB) * multiplier
    } else if ((valueA as any) instanceof Date && (valueB as any) instanceof Date) {
      return (valueA.getTime() - valueB.getTime()) * multiplier
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      return (new Date(valueA).getTime() - new Date(valueB).getTime()) * multiplier
    }

    return 0
  }

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const formatDate = (date: string | number | Date, fromToday: boolean = true): string => {
  const now = new Date().getTime()
  const dateTime = new Date(date).getTime()
  if (isNaN(dateTime)) {
    console.error('Invalid date:', date)
    return ''
  }
  const dateA = fromToday ? dateTime : now
  const dateB = fromToday ? now : dateTime
  return formatDistance(dateA, dateB, {
    addSuffix: true,
    locale: getLocale(),
  })
}

export const findLastChatSessionId = (
  chatSessions: { id: string; startedAt: TimeType }[],
): string | undefined => {
  const now = new Date()
  return chatSessions
    .filter((cs) => cs.startedAt)
    .map((cs) => ({
      id: cs.id,
      // Find the chatSessionId with startedAt closest to today
      diff: Math.abs(differenceInMilliseconds(now, new Date(cs.startedAt))),
    }))
    .sort((a, b) => a.diff - b.diff)[0]?.id
}
